//@ts-nocheck
import { useState, useEffect, useRef, useCallback } from 'react';
import { Loader } from '@googlemaps/js-api-loader';

const PLACES_API_KEY = import.meta.env.VITE_GOOGLE_API_KEY;

export const usePlacesAutocomplete = () => {
  const autocompleteService =
    useRef<google.maps.places.AutocompleteService | null>(null);
  const placesService = useRef<google.maps.places.PlacesService | null>(null);

  const [predictions, setPredictions] = useState<
    google.maps.places.AutocompletePrediction[]
  >([]);
  const [loading, setLoading] = useState(false);

  // Effect to load the Google Maps script and initialize services
  useEffect(() => {
    if (!PLACES_API_KEY) {
      console.error('Google Maps API key is missing.');
      return;
    }

    const loader = new Loader({
      apiKey: PLACES_API_KEY,
      version: 'weekly',
      libraries: ['places'],
    });

    loader
      .importLibrary('places')
      .then((places) => {
        autocompleteService.current = new places.AutocompleteService();
        // The div is required by the PlacesService constructor
        placesService.current = new places.PlacesService(
          document.createElement('div'),
        );
      })
      .catch((e) => {
        console.error('Failed to load Google Maps script:', e);
      });
  }, []);

  // Function to fetch autocomplete predictions
  const fetchPredictions = useCallback((input: string) => {
    if (!autocompleteService.current) {
      return;
    }
    if (!input) {
      setPredictions([]); // Clear suggestions if input is empty
      return;
    }

    setLoading(true);

    autocompleteService.current.getPlacePredictions(
      { input },
      (results, status) => {
        setLoading(false);
        if (status === 'OK' && results) {
          setPredictions(results);
        } else if (status === 'ZERO_RESULTS') {
          setPredictions([]);
        } else {
          setPredictions([]);
        }
      },
    );
  }, []);

  // Function to get detailed information about a specific place
  const getPlaceDetails = useCallback(async (placeId, callback) => {
    if (!placesService.current) {
      return;
    }
    placesService.current.getDetails(
      {
        placeId,
        fields: ['name', 'geometry', 'formatted_address', 'address_components'],
      },
      callback,
    );
  }, []);

  // Function to clear suggestions manually
  const clearPredictions = () => {
    setPredictions([]);
  };

  return {
    predictions,
    loading,
    fetchPredictions,
    getPlaceDetails,
    clearPredictions,
  };
};
